/**
 * OAuth2 Utilities for Admin Authentication
 *
 * Implements OAuth2 Authorization Code Flow with PKCE (RFC 7636)
 * for admin authentication with TPOS Account integration
 */

import axios from "axios";
import type {
  OAuth2InitiateRequest,
  OAuth2InitiateResponse,
  OAuth2CallbackRequest,
  OAuth2SessionData,
  AdminAuthResponse,
} from "@/types/oauth";

/**
 * Get API Base URL
 * Priority:
 * 1. Runtime config from window.APP_CONFIG (Kubernetes ConfigMap in production)
 * 2. Environment variable VITE_API_BASE_URL (build-time configuration)
 * 3. Empty string for relative URLs (development with Vite proxy)
 */
const getApiBaseUrl = (): string => {
  // Production: Use runtime config from Kubernetes ConfigMap
  if (window.APP_CONFIG?.apiUrl) {
    return window.APP_CONFIG.apiUrl;
  }

  // Build-time: Use environment variable if set
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl) {
    return envUrl;
  }

  // Development: Use relative URL (Vite proxy handles routing to localhost:5101)
  return "";
};

const API_BASE_URL = getApiBaseUrl();

// Create dedicated axios client for OAuth2 (not using api wrapper)
const oauth2Client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * OAuth2 providers that exist only for local development.
 *
 * `localhost-oauth` (displayed as "Account.Tpos.dev") is a developer sign-in route: it is
 * configured on dev and staging and switched off in production, so every deployed environment
 * either offers a login nobody outside the team should use or a button that cannot work.
 * It stays on the page only when the console is being served from a developer's own machine.
 */
const LOCAL_ONLY_OAUTH_PROVIDERS = new Set(["localhost-oauth"]);

/** Hostnames that mean "this console is running on the developer's own machine". */
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

/**
 * Decides whether a provider returned by the backend should be offered on the sign-in screen.
 *
 * Keyed on the HOSTNAME, not on `import.meta.env.DEV`: a development build served from a
 * cluster (dev/staging run exactly that) is still a deployed environment, so a build-mode check
 * would keep the button visible in the two places this is meant to remove it from.
 */
export const isOAuthProviderVisible = (providerName: string): boolean =>
  !LOCAL_ONLY_OAUTH_PROVIDERS.has(providerName) ||
  LOCAL_HOSTNAMES.has(window.location.hostname);

/**
 * OAuth2 Provider Configuration
 */
export const OAUTH2_SESSION_KEY = "wicloud_admin_oauth2_session";
export const SESSION_EXPIRY_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Standard API Response Wrapper from Backend
 */
interface StandardResponse<T> {
  success: boolean;
  error: null | {
    code: string;
    message: string;
    details?: unknown;
  };
  data: T;
}

/**
 * PKCE Utilities (RFC 7636)
 */
const base64URLEncode = (buffer: ArrayBuffer): string => {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};

export const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array.buffer);
};

export const generateCodeChallenge = async (
  verifier: string,
): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64URLEncode(digest);
};

export const isCryptoSupported = (): boolean => {
  return !!(
    window.crypto &&
    window.crypto.subtle &&
    window.crypto.getRandomValues
  );
};

export const validateState = (
  receivedState: string,
  storedState: string,
): boolean => {
  return receivedState === storedState;
};

/**
 * OAuth2 API Service
 */
export const adminOAuth2Service = {
  /**
   * Initiate OAuth2 flow - gets authorization URL from backend
   */
  async initiate(data: OAuth2InitiateRequest): Promise<OAuth2InitiateResponse> {
    const response = await oauth2Client.post<
      StandardResponse<OAuth2InitiateResponse>
    >("/api/v1/admin/auth/oauth2/initiate", data);
    return response.data.data;
  },

  /**
   * Handle OAuth2 callback - exchange authorization code for tokens
   */
  async callback(data: OAuth2CallbackRequest): Promise<AdminAuthResponse> {
    const response = await oauth2Client.post<
      StandardResponse<AdminAuthResponse>
    >("/api/v1/admin/auth/oauth2/callback", {
      provider: data.provider,
      code: data.code,
      state: data.state,
      codeVerifier: data.codeVerifier,
      redirectUri: data.redirectUri,
    });
    return response.data.data;
  },

  /**
   * Get available OAuth2 providers
   */
  async getProviders(): Promise<{ name: string; displayName: string }[]> {
    const response = await oauth2Client.get<
      StandardResponse<{ name: string; displayName: string }[]>
    >("/api/v1/admin/auth/oauth2/providers");
    return response.data.data;
  },
};

/**
 * Session Storage Utilities
 */
export const oauth2SessionStorage = {
  save(data: OAuth2SessionData): void {
    localStorage.setItem(OAUTH2_SESSION_KEY, JSON.stringify(data));
  },

  get(): OAuth2SessionData | null {
    const data = localStorage.getItem(OAUTH2_SESSION_KEY);
    return data ? JSON.parse(data) : null;
  },

  clear(): void {
    localStorage.removeItem(OAUTH2_SESSION_KEY);
  },

  isExpired(session: OAuth2SessionData): boolean {
    return Date.now() - session.timestamp > SESSION_EXPIRY_MS;
  },
};
